import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SphereIndicator, SphereIndicatorsResponse } from 'types/metric';
import type { SphereSectionSlotDataSource } from '../../sphereSectionSlots';

export type SphereIndicatorsState =
  | { status: 'idle' }
  /** `refreshFailed`: a re-read failed after this load, so the cards show the last indicators that loaded. A read that
   * starts from that state keeps it until it settles, so the notice and its "Try again" stay put while it runs. */
  | {
      status: 'loading';
      indicators?: SphereIndicator[];
      refreshFailed?: true;
      /** A read that starts from `failed`, or from a `loading` that carries a count, keeps it, so the section keeps saying it failed while the retry runs. */
      failedReads?: number;
    }
  | { status: 'loaded'; indicators: SphereIndicator[]; refreshFailed?: true }
  /** `failedReads`: consecutive failed reads with nothing to show, so a retry that fails again can say it ran. */
  | { status: 'failed'; failedReads: number };

/** A second `load` inside this window reuses the first read. */
const SPHERE_INDICATORS_REUSE_MS = 30_000;

const IDLE: SphereIndicatorsState = { status: 'idle' };

interface Entry {
  state: SphereIndicatorsState;
  loadedAt: number;
  requestId: number;
  client?: ApiClient;
}

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let nextRequestId = 0;

const notify = () => listeners.forEach((listener) => listener());

/** The indicators already on screen, which a re-read keeps showing while it runs and when it fails. */
export const shownIndicators = (state: SphereIndicatorsState): SphereIndicator[] | undefined =>
  state.status === 'loaded' || state.status === 'loading' ? state.indicators : undefined;

function startRead(sphereId: string, client: ApiClient, shown?: SphereIndicatorsState): void {
  const requestId = ++nextRequestId;
  const previous = shown ? shownIndicators(shown) : undefined;
  const refreshFailed = previous && shown && 'refreshFailed' in shown && shown.refreshFailed ? true : undefined;
  // A read that starts while a retry of a failed read runs (an `invalidate` after a save) keeps its count.
  const failedReads = shown?.status === 'failed' || shown?.status === 'loading' ? shown.failedReads : undefined;
  let loading: SphereIndicatorsState = { status: 'loading', indicators: previous };
  if (failedReads !== undefined) loading = { status: 'loading', failedReads };
  else if (refreshFailed) loading = { status: 'loading', indicators: previous, refreshFailed };
  entries.set(sphereId, {
    state: loading,
    loadedAt: 0,
    requestId,
    client,
  });
  notify();

  client
    .get<SphereIndicatorsResponse>(`/spheres/${encodeURIComponent(sphereId)}/indicators`)
    .then((response) => {
      const current = entries.get(sphereId);
      if (current?.requestId !== requestId) return;
      entries.set(sphereId, {
        state: { status: 'loaded', indicators: response?.indicators ?? [] },
        loadedAt: Date.now(),
        requestId,
        client,
      });
      notify();
    })
    .catch(() => {
      const current = entries.get(sphereId);
      if (current?.requestId !== requestId) return;
      // A failed re-read keeps what loaded before and says so; only a read with nothing to show fails the section.
      // `loadedAt` stays 0, so the next `load` reads again instead of reusing the stale indicators.
      const state: SphereIndicatorsState = previous
        ? { status: 'loaded', indicators: previous, refreshFailed: true }
        : { status: 'failed', failedReads: (failedReads ?? 0) + 1 };
      entries.set(sphereId, { state, loadedAt: 0, requestId, client });
      notify();
    });
}

/**
 * Per-sphere cache of `GET /spheres/:id/indicators`. Issues GET requests only (the read is pure).
 * `peek` returns a referentially stable snapshot so `useSyncExternalStore` and the slot's
 * synchronous `appliesTo` / `ownsProperty` can read it.
 */
export const sphereIndicatorsStore: SphereSectionSlotDataSource & {
  peek(sphereId: string): SphereIndicatorsState;
  reset(): void;
} = {
  peek(sphereId) {
    return entries.get(sphereId)?.state ?? IDLE;
  },

  load({ sphereId, graphClient, force }) {
    const entry = entries.get(sphereId);
    // A different client is a different signed-in identity: its permissions may differ, so
    // another caller's numbers are neither reused nor shown while the new read is in flight.
    const sameCaller = entry?.client === graphClient;
    if (entry && sameCaller && !force) {
      if (entry.state.status === 'loading') return;
      if (entry.state.status === 'loaded' && Date.now() - entry.loadedAt < SPHERE_INDICATORS_REUSE_MS) return;
    }
    startRead(sphereId, graphClient, sameCaller && entry ? entry.state : undefined);
  },

  subscribe(listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** After a save the indicators are stale: re-read with the client that loaded them, or drop the entry. */
  invalidate(sphereId) {
    const entry = entries.get(sphereId);
    if (!entry) return;
    if (entry.client) {
      startRead(sphereId, entry.client, entry.state);
      return;
    }
    entries.delete(sphereId);
    notify();
  },

  /** Test seam: forget every sphere. */
  reset() {
    entries.clear();
    notify();
  },
};
